import { IPerson, IIDAndName, IIDAndLabel, ICustomField, IPhoto } from '..'
import { IHelpInfo } from './CustomFields'
import { IEmailFields } from './EmailTypes'
import { IPagination } from './Entity'
import { IHSCustomField } from './HSFields'

export type IncidentExcludableField = 'hazardsList'

export interface IIncidentEmailResponse {
  message: IEmailFields
}

export enum IncidentStatus {
  Pending = 100,
  Open = 200,
  Resolved = 300
}

export interface IIncident {
  id: number | null
  type: IIDAndName
  ticketsNo?: string
  incidentNo: null | number
  status: IncidentStatus
  site: null | IIDAndName
  name: string
  franchise?: IIDAndName
  customFields?: IHSCustomField[]
  conversations?: IIncidentComment[]
  [fieldName: string]: any
  people: IPerson[] | null
  hardAssociation: boolean
  personFields?: IIncidentPersonFields | null
  incidentCategory?: IIDAndName
  path?: string
  hazard?: IIDAndName
  incidentTypePermission: IIncidentTypePermission
  deletedPhotos?: number[]
}

export interface IIncidentEmailOptions {
  sendPDF: boolean
  sendURLSign: boolean
  invalidateURL?: boolean
}

export interface IIncidentComment {
  name: string
  date: string
  comment: string
}

export interface IIncidentTypeCustomField {
  incidentDetailTab: boolean
  showField: boolean
  sortOrder: null | number
  id: number
  customField: IIDAndName
  incidentType: IIDAndName
}

export interface IIncidentType {
  id: number
  name: string
  incidentTypeCustomFields: IIncidentTypeCustomField[]
  incidentCustomFieldsByType: IHSCustomField[]
  [fieldName: string]: any
  personFieldsConfiguration: IIncidentTypePersonFieldsConfiguration
  peopleSelectionRequired?: boolean
  allowPhotos: boolean
  allowVideos: boolean
  allowAttachments: boolean
}

export interface IIncidentStatus {
  label: string
  value: number
}

export interface IIncidentResponse {
  attachments: IPhoto[]
  incident: IIncident
  showInvestigationTab: boolean
  hazardsList: IIDAndName[] | null
  helpInfos: IHelpInfo[]
  healthSafetyClassList: IIDAndLabel[]
  severityOptionsList: string[]
  injuryTypeOptionsList: string[]
  badCouldBeOptionsList: string[]
  chanceHappenAgainOptionsList: string[]
  incidentTypesList: IIncidentType[]
  photoCanvasTemplates: IHSCustomField[]
  incidentChangeTracker: any // comes back with existing incident data
  availableStatuses: IIncidentStatus[]
  detailsCategories?: IIncidentCategory[] // categories displayed on details tab (available for reporter)
  categories: IIncidentCategory[] // categories displayed on investigation tab
  emailFieldMapping?: IIncidentEmailFieldMapping
}

export interface IIncidentsListEntryWrapper {
  incident: IIncidentsListEntry
}

export interface IIncidentsListEntry {
  name: string
  type: string
  desc: string
  site: IIDAndName
  franchisee: IIDAndName
  id: number
  incidentNo: string
  status: number
  incidentCategory?: IIDAndName
  date: string
  versionEnum?: string
}

export interface IIncidentCategory {
  description: string
  id: number
  label: string
  openers: IIDAndName[]
  resolvers: IIDAndName[]
  automaticResolve?: boolean // automatic resolve incident (show all recStatus btns)
  automaticOpen?: boolean // automatic open
  showInDetails?: boolean // display in details tab?
  showInvTab?: boolean // show investigation tab for all users(this overwrites normal user's profile permission)
  availableStatuses?: IIncidentStatus[]
}

export interface IIncidentCategories {
  categories: IIncidentCategory[]
}

export interface IIncidentTypes {
  incidentTypes: IIDAndName[]
}

export interface IIncidentSearchParams {
  name: string
  siteId?: number
  incidentTypeId?: number
  fromDate?: Date | string
  toDate?: Date | string
  selectedPeople?: string[]
  franchiseeId?: number
  status?: string | number // This status pertains to Pending (100), Open (200), and Resolved (300).
  signed?: boolean
  recStatus?: string // This is the recStatus of the incident.
  siteStatus?: string // This is the recStatus of the site to which the incident is linked.
}

export interface IIncidentTypePersonFieldsConfiguration {
  id: number
  firstName: boolean
  midName: boolean
  lastName: boolean
  email: boolean
  homePhoneNumber: boolean
  workPhoneNumber: boolean
  mobileNumber: boolean
  externalId: boolean
  customFieldsJson: IHSCustomField[]
  anyFieldSelected: boolean
}

export interface IIncidentPersonFields {
  id: number
  personId: number
  firstName: string
  midName: string
  lastName: string
  email: string
  workPhoneNumber: string
  homePhoneNumber: string
  mobileNumber: string
  externalId: string
  customFields: ICustomField[] // from oneplacecomponents
}

export interface IIncidentEmailFieldMapping {
  id: number
  email: boolean
  customFieldIds: number[]
}

export const incidentStatusLabels = [
  'customLabel_incidentStatusPending',
  'customLabel_incidentStatusOpen',
  'customLabel_incidentStatusResolved'
]
export const incidentStatusCodes = [100, 200, 300]

export function findIncidentStatus(status: number): string {
  if (!status) {
    return ''
  }
  const activeStepIdx = incidentStatusCodes.indexOf(status)
  return incidentStatusLabels[activeStepIdx]
}

export interface IIncidentTicketsCount {
  incidentTicketCount: number
}

export interface IIncidentTypePermission {
  profile: IIDAndName
  incidentType: IIDAndName
  accessIncidentCategory: boolean
  accessIncidentInvestigation: boolean
  allowSendIncidentEmail: boolean
  accessIncidentsAllTime: boolean
  selected: boolean
  entityId: number //profile id or incidentType id
}

export interface IIncidentEmail {
  emailEditorData: IEmailEditorData
  sendPDF: boolean
  sendURL: boolean
  invalidateURL: boolean
}

export interface IEmailEditorData {
  emailAdd: string
  ccEmailAdd: string
  emailTitle: string
  emailContent: string
}

export interface IIncidentConversation {
  id: number
  name: string
  comment: string
  date: string
}

export interface IIncidentConversationsWrapper {
  conversations: IIncidentConversation[]
  pagination?: IPagination
}

export interface IIncidentConversationsCount {
  conversationsCount: number
}

export const INCIDENT_COMMENTS_PAGE_SIZE = 10
